"use client"

import { useState } from "react"
import { ChevronLeft, ChevronRight, Star } from "lucide-react"
import { cn } from "@/lib/utils"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"

export function WhatUsersSay() {
  const [activeIndex, setActiveIndex] = useState(0)

  const testimonials = [
    {
      quote:
        "CareAdvisor saved us hours of regulatory research. It gave us immediate access to Minnesota-specific compliance information with exact statute references.",
      author: "Jennifer Lawson",
      role: "Director of Operations",
      company: "Northland Care Services",
      rating: 5,
    },
    {
      quote:
        "Before CareAdvisor, I spent hours trying to interpret complex regulations. Now I get clear answers with citations to Minnesota statutes in seconds.",
      author: "Michael Chen",
      role: "Compliance Officer",
      company: "Twin Cities Home Care",
      rating: 5,
    },
    {
      quote:
        "During our last audit, CareAdvisor helped us quickly verify our policies against current Minnesota requirements. The source citations were invaluable.",
      author: "Sarah Johnson",
      role: "Program Director",
      company: "Metro Assisted Living",
      rating: 5,
    },
  ]

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))
  }

  return (
    <section id="what-users-say" className="relative py-24 md:py-32 bg-gradient-to-b from-white to-gray-50 dark:from-[#1E293B] dark:to-[#0F172A] overflow-hidden scroll-mt-20">
      
    
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl opacity-30 dark:opacity-20" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-full blur-3xl opacity-30 dark:opacity-20" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern-light dark:bg-grid-pattern-dark opacity-20 dark:opacity-10" />
      </div>



      <div className="container px-4 md:px-8 lg:px-12 mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
          <div className="inline-flex items-center px-4 py-1 rounded-full text-sm font-medium bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light mb-4 backdrop-blur-sm">
            WHAT USERS SAY
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground dark:text-white mb-6 tracking-tight">
            Trusted by Minnesota <span className="text-primary dark:text-primary-light">Providers</span>
          </h2>
          <p className="text-xl text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto">
            Hear from professionals who use CareAdvisor to get reliable compliance guidance based on authoritative Minnesota sources.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Card glow effect */}
            <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/30 dark:to-primary-light/30 opacity-70 blur-xl"></div>
            
            <div className="relative bg-white dark:bg-[#1E293B] rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800/50 p-8 md:p-10">
              <div className="absolute top-0 right-0 -mt-4 -mr-4">
                <div className="bg-primary text-white dark:bg-primary dark:text-white text-sm font-medium py-1 px-3 rounded-full shadow-md">
                  Verified Review
                </div>
              </div>

              <div className="mb-6 flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                ))}
              </div>

              <div className="relative min-h-[200px] overflow-visible">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className={cn(
                      "transition-all duration-500 ease-in-out flex flex-col justify-between",
                      index === activeIndex
                        ? "block opacity-100 sm:transform-none"
                        : "hidden sm:block opacity-0 sm:translate-x-8",
                      "sm:absolute sm:inset-x-0 sm:top-0 sm:bottom-8"
                    )}
                    aria-hidden={index !== activeIndex}
                  >
                    <blockquote className="text-xl md:text-2xl text-foreground dark:text-white font-medium">
                      &ldquo;{testimonial.quote}&rdquo;
                    </blockquote>

                    <div className="flex items-center">
                      <Avatar className="[--avatar-size:2rem] sm:[--avatar-size:2.5rem] md:[--avatar-size:3rem]">
                        <AvatarFallback>
                          {testimonial.author.split(" ").map((n) => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="ml-2 sm:ml-3 md:ml-4">
                        <p className="font-medium text-foreground dark:text-white">{testimonial.author}</p>
                        <p className="text-muted-foreground dark:text-gray-400 text-sm">
                          {testimonial.role}, {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="absolute -bottom-5 right-10 flex space-x-3">
                <button
                  onClick={handlePrev}
                  className="h-10 w-10 rounded-full bg-white dark:bg-[#1E293B] border border-gray-200 dark:border-gray-800/50 shadow-md flex items-center justify-center text-foreground dark:text-white hover:bg-primary hover:border-primary hover:text-white dark:hover:bg-primary dark:hover:border-primary dark:hover:text-white transition-colors"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={handleNext}
                  className="h-10 w-10 rounded-full bg-white dark:bg-[#1E293B] border border-gray-200 dark:border-gray-800/50 shadow-md flex items-center justify-center text-foreground dark:text-white hover:bg-primary hover:border-primary hover:text-white dark:hover:bg-primary dark:hover:border-primary dark:hover:text-white transition-colors"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 flex justify-center">
            <div className="flex space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === activeIndex ? "bg-primary dark:bg-primary-light w-8" : "bg-gray-300 dark:bg-gray-700 w-2"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === activeIndex ? "true" : "false"}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      

    </section>
  )
} 